import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, firstValueFrom, of } from 'rxjs';
import { environment } from '../../../../environments/environment';

import Paystack from '@paystack/inline-js';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {

  private paystackInstance: any; // Using any to avoid type issues with Paystack
  private apiUrl = `${environment.apiUrl}/api/payment`;

  constructor(private http: HttpClient) {

    if (environment.paystackPublicKey) {
      this.paystackInstance = new (Paystack as any)();
    }
  }

  getPaystackInstance() {
    if (!this.paystackInstance) {
      this.paystackInstance = new (Paystack as any)();
    }
    return this.paystackInstance;
  }



  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }



  /**
   * Get all available Paystack subscription plans
   * @returns Observable with Paystack plans data
   */
  getPaystackPlans(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/api/Subscription/paystack/plans`);
  }

  async createSubscription(planId: string, paymentMethod: string): Promise<any> {
    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${environment.apiUrl}/api/Subscription/paystack/subscription`, {
          planId,
          paymentMethod
        })
      );
      return response;
    } catch (error) {
      //console.error('Subscription creation error:', error);
      throw error;
    }
  }

  verifyPaystackPayment(reference: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/api/Subscription/paystack/verify/${reference}`);
  }

  setupPaymentIntent(options?: { headers?: HttpHeaders }): Observable<any> {
    return this.http.post(`${environment.apiUrl}/api/Subscription/SetupPaymentIntent`, null, options);
  }

  /**
   * Enable/Renew a Paystack subscription
   * @param subscriptionCode The Paystack subscription code
   * @param emailToken The email token for verification
   * @returns Observable with enablement result
   */
  enablePaystackSubscription(subscriptionCode: string, emailToken: string): Observable<any> {
    const headers = this.getAuthHeaders().set('Content-Type', 'application/json');
    return this.http.post(
      `${environment.apiUrl}/api/Subscription/paystack/enable`,
      { code: subscriptionCode, token: emailToken },
      { headers }
    );
  }

  /**
   * Cancel a Paystack subscription
   * @param subscriptionCode The Paystack subscription code
   * @param emailToken The email token for verification
   * @returns Observable with cancellation result
   */
  cancelPaystackSubscription(subscriptionCode: string, emailToken: string): Observable<any> {
    const headers = this.getAuthHeaders().set('Content-Type', 'application/json');
    return this.http.post(
      `${environment.apiUrl}/api/Subscription/paystack/disable`,
      { code: subscriptionCode, token: emailToken },
      { headers }
    );
  }

  /**
   * Cancel subscription (generic method that will use Paystack if configured)
   * @param subscriptionCode Optional Paystack subscription code
   * @param emailToken Optional Paystack email token
   * @returns Observable with cancellation result
   */
  cancelSubscription(subscriptionCode?: string, emailToken?: string): Observable<any> {
    if (environment.paystackPublicKey && subscriptionCode && emailToken) {
      return this.cancelPaystackSubscription(subscriptionCode, emailToken);
    }
    // Fall back to Stripe cancellation if Paystack is not configured
    return this.http.post(`${environment.apiUrl}/api/Subscription/Cancel`, {});
  }

  /**
   * Cancel trial locally on the backend (no provider subscription code required)
   */
  cancelTrial(): Observable<any> {
    const headers = this.getAuthHeaders().set('Content-Type', 'application/json');
    return this.http.post(`${environment.apiUrl}/api/Subscription/CancelTrial`, {}, { headers });
  }


  /**
   * Renew/Enable a subscription
   * @param subscriptionCode Optional Paystack subscription code
   * @param emailToken Optional Paystack email token
   * @returns Observable with renewal result
   */
  renewSubscription(subscriptionCode?: string, emailToken?: string): Observable<any> {
    if (environment.paystackPublicKey && subscriptionCode && emailToken) {
      return this.enablePaystackSubscription(subscriptionCode, emailToken);
    }
    // Fall back to Stripe renewal if Paystack is not configured
    return this.http.post(`${environment.apiUrl}/api/Subscription/Renew`, {});
  }

  activateSubscription(): Observable<any> {
    return this.http.post(`${environment.apiUrl}/api/Subscription/Activate`, {});
  }

  /**
   * Generate a Paystack subscription update link
   * @param subscriptionCode The Paystack subscription code
   * @returns Observable with the update link
   */
  generateSubscriptionUpdateLink(subscriptionCode: string): Observable<{ status: boolean; message: string; data: { link: string } }> {
    const headers = this.getAuthHeaders().set('Content-Type', 'application/json');
    return this.http.get<{ status: boolean; message: string; data: { link: string } }>(
      `https://api.paystack.co/subscription/${subscriptionCode}/manage/link`,
      { headers }
    );
  }
}
